import { useInView } from 'react-intersection-observer';

import { theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import { SampleFormulationCardCarousel } from 'Apps/Products/Components/SampleFormulationCardCarousel';

import { ExpertsModule } from 'Components/ExpertsModule';
import { MetaData } from 'Components/MetaData';

import { Faq } from 'Blocks/Faq';
import { HowItWorks } from 'Blocks/HowItWorks';
import { Ticker } from 'Blocks/Ticker';

import ScrollTracker from 'Containers/ScrollTracker';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';

import { faq, sampleFormulationModule, ticker } from 'assets/content/landingPages/skincare';
import { skincareLPStructuredData } from 'assets/metadata/skincareLPStructuredData';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { useFetchProductsList } from 'dux/products/hooks';

import { BeforeAndAfterSection } from './Skincare/BeforeAndAfterSection';
import { ClinicalClaims } from './Skincare/ClinicalClaims';
import { DeriskingModule } from './Skincare/DeriskingModule';
import { IntroductionSection } from './Skincare/IntroductionSection';
import SkincareConsultationCTA from './Skincare/SkincareConsultationCTA';
import { SkincareProductGrid } from './Skincare/SkincareProductGrid';

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Border = styled.div`
  width: 100%;
  height: 1px;
  background-color: black;
`;

type SkincareLandingProps = {
  banner: boolean;
  footer: boolean;
  header: boolean;
};

const SkincareLanding = ({ header = true, footer = true, banner = true }: SkincareLandingProps) => {
  const { isMobile } = useResponsiveVariant();
  const [introSectionRef, inView] = useInView({ initialInView: true });

  // INFO: product prices are used on this page
  useFetchProductsList();

  return (
    <>
      <MetaData
        description="Get custom skin care formulated to meet all of your unique skin needs and concerns. Prose Custom Cleanser, Serum and Moisturizer are made just for you!"
        inheritParentCanonicalURL
        structuredData={skincareLPStructuredData}
        title="Custom Skin Care Products | Prose"
      />
      <BaseScene background="none" banner={banner} display="block" footer={footer} header={header}>
        <ScrollTracker>
          <FlexWrapper>
            <IntroductionSection ref={introSectionRef} />
            <Ticker content={ticker} hasBorder />
            <SkincareProductGrid />
            <SampleFormulationCardCarousel
              cards={sampleFormulationModule.cards}
              subTitle={sampleFormulationModule.subTitle}
              title={sampleFormulationModule.title}
            />
            <Border />
            <BeforeAndAfterSection />
            <Border />
            <ClinicalClaims />
            <Border />
            <HowItWorks cta dataFrom="block-how-it-works" isSkincareOnly />
            <Border />
            <DeriskingModule />
            <Border />
            <ExpertsModule />
            <Faq
              backgroundColor={theme.colors.primary[300]}
              dataTestId="skincare-lp-faq"
              hasBorder
              markupName="h3"
              questions={faq.questions}
              title={faq.title}
              variant="dark"
            />
          </FlexWrapper>
          {!inView && isMobile && (
            <SkincareConsultationCTA dataTestId="skincare-lp-fixed-cta" isFixed />
          )}
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

export default SkincareLanding;
